/**
 * IncidentTimeline -- Renders a chronological list of messages.
 * Supports both evidence (flagged) and context (non-flagged) messages.
 * Evidence messages are highlighted; context messages are subdued.
 */
import { format } from 'date-fns';
import { Image, Video, Mic, FileText, Sticker, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { type IncidentMessage, type MessageType, getScoreColorClass } from '@/lib/incidentUtils';

// Extended message type that may include context_type and event_explanation from timeline subcollection
interface TimelineMessageDisplay extends IncidentMessage {
  context_type?: 'evidence' | 'context';
  event_explanation?: string;
}

interface IncidentTimelineProps {
  messages: TimelineMessageDisplay[];
  className?: string;
}

function MessageContentRenderer({ msg }: { msg: TimelineMessageDisplay }) {
  const senderLabel = msg.fromMe ? 'You' : (msg.pushName || 'Unknown');
  const isEvidence = msg.context_type === 'evidence';

  const mediaIcons: Record<string, React.ReactNode> = {
    image: <Image className="h-4 w-4" />,
    video: <Video className="h-4 w-4" />,
    audio: <Mic className="h-4 w-4" />,
    document: <FileText className="h-4 w-4" />,
    sticker: <Sticker className="h-4 w-4" />,
  };

  const mediaLabels: Record<string, string> = {
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    document: 'Document',
    sticker: 'Sticker',
  };

  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-center gap-1.5">
        <span className={cn(
          "text-xs font-medium",
          isEvidence ? "text-foreground" : "text-muted-foreground"
        )}>
          {senderLabel}
        </span>
        {isEvidence && (
          <ShieldAlert className="h-3 w-3 text-red-500" />
        )}
      </div>
      {msg.messageType === 'text' || !msg.messageType ? (
        msg.content ? (
          <p className={cn(
            "text-sm line-clamp-3",
            isEvidence ? "text-foreground/80" : "text-muted-foreground"
          )}>
            {msg.content}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground italic">No content available</p>
        )
      ) : (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {mediaIcons[msg.messageType] ?? <FileText className="h-4 w-4" />}
          <span>{mediaLabels[msg.messageType] ?? msg.messageType}</span>
          {msg.content && (
            <span className="text-xs text-foreground/60">— {msg.content}</span>
          )}
        </div>
      )}
    </div>
  );
}

export function IncidentTimeline({ messages, className }: IncidentTimelineProps) {
  if (messages.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No message details available for this incident.
      </div>
    );
  }

  return (
    <div className={cn("relative pl-6", className)}>
      <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-border" />

      {messages.map((msg) => {
        const isEvidence = msg.context_type === 'evidence';

        return (
          <div key={msg.message_id} className="relative mb-3 last:mb-0">
            <div className={cn(
              "absolute -left-4 top-1 h-3 w-3 rounded-full border-2 border-background",
              isEvidence
                ? (msg.severity_score >= 7 ? "bg-red-500" :
                   msg.severity_score >= 5 ? "bg-yellow-500" : "bg-orange-400")
                : "bg-gray-300 dark:bg-gray-600"
            )} />
            <Card className={cn(
              "p-3",
              isEvidence
                ? "border-red-200 dark:border-red-900/50"
                : "border-transparent bg-muted/30"
            )}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">
                  {format(msg.timestamp, 'MMM d, HH:mm')}
                </span>
                {isEvidence && msg.severity_score > 0 && (
                  <Badge className={getScoreColorClass(msg.severity_score / 10)}>
                    Score: {msg.severity_score.toFixed(1)}
                  </Badge>
                )}
              </div>
              <MessageContentRenderer msg={msg} />
              {isEvidence && msg.event_explanation && (
                <p className="text-xs text-orange-700 dark:text-orange-300 mt-1.5 italic">
                  → {msg.event_explanation}
                </p>
              )}
              {isEvidence && msg.flagged_categories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {msg.flagged_categories.map((cat, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{cat}</Badge>
                  ))}
                </div>
              )}
            </Card>
          </div>
        );
      })}
    </div>
  );
}
